import moment from "moment";
import {
  ActivityResult,
  InputValue,
  LineHandling,
  Nomination,
  Activity,
  ProcessAction,
  ProcessStep,
  ProcessConfig,
  Conditions,
  StepCondition,
  StepConditions,
  InputValueValue
} from "../models";
import { PersistentDataService } from "./PersistentDataService";

export class SofService {
  activityResult: ActivityResult;

  lineHandling: LineHandling;
  nomination?: Nomination | null;
  activities: Activity[];
  activity: Activity;

  lineHandlingNo: string;
  nominationNo: string;
  actionId: number;

  config: ProcessConfig;
  step: ProcessStep;
  action: ProcessAction;

  executionTime: string;

  constructor() {
    this.activityResult = {} as ActivityResult;
    this.lineHandlingNo = "0";
    this.nominationNo = "0";
    this.lineHandling = {} as LineHandling;
    this.config = {} as ProcessConfig;
    this.step = {} as ProcessStep;
    this.action = {} as ProcessAction;
    this.activities = [];
    this.activity = {} as Activity;
    this.actionId = 0;
    this.executionTime = "";
  }

  public async initilizeLineHandling(
    lineHandlingNo: string,
    nominationNo: string
  ) {
    this.lineHandlingNo = lineHandlingNo;
    this.nominationNo = nominationNo;

    const lineHandling = await PersistentDataService.getLineHandlingAsync(
      lineHandlingNo
    );
    if (!lineHandling)
      throw new Error(
        `No offline data for the Line Handling no ${lineHandlingNo}`
      );
    this.lineHandling = lineHandling;

    if (nominationNo === "0") {
      this.nomination = null;
      this.activityResult.newState = lineHandling.currentState;
    } else {
      this.nomination = lineHandling.nominations.find(
        f => f.nominationNo === nominationNo
      );
      if (this.nomination)
        this.activityResult.newState = this.nomination?.currentState;
    }
  }

  public async initializeAction(
    activityId: number,
    actionId: number,
    surveyor?: string
  ) {
    const config = await PersistentDataService.getConfigurationAsync();
    if (!config) throw new Error("No offline configuration data exists.");
    this.config = config;

    const activities = await PersistentDataService.getActivities(
      this.lineHandlingNo
    );
    if (!activities)
      throw new Error(
        `No offline activities for the Line Handling no ${this.lineHandling}`
      );
    this.activities = activities;
    let activity = activities.find(f => f.id === activityId);
    if (!activity) {
      const tryActivityId = this.tryGetActivityId(actionId, surveyor);
      if (tryActivityId)
        activity = activities.find(f => f.id === tryActivityId);
    }
    if (!activity) throw new Error(`The current activity does not exists.`);
    this.activity = activity;

    this.actionId = actionId;
    const step = config.processSteps.find(
      f => f.id === this.activity.processStepId
    );
    if (!step)
      throw new Error("The process step does not exists in the configuration.");
    this.step = step;

    const action = step.processActions.find(f => f.id === actionId);
    if (!action)
      throw new Error(
        "The process action does not exists in the configuration."
      );
    this.action = action;
  }

  public async executeAction(
    executionTime: string,
    inputValues: InputValueValue[] = []
  ): Promise<string> {
    this.checkActionPreCondition();

    const actionDesc = this.replaceMergeFields(this.action.name);
    const actionType = this.action.type;

    // update ActivityResult
    this.activityResult.actionDescription = actionDesc;
    this.executionTime = executionTime;

    this.activity.startedOn =
      actionType === "Start" ? executionTime : this.activity.startedOn;
    this.activity.finishedOn =
      actionType === "End" || actionType === "Skip"
        ? executionTime
        : this.activity.finishedOn;

    this.activity.description = actionDesc;
    this.activity.performedBy = ""; //currentuser
    this.activity.status = this.getActivityStatus(actionType);

    if (this.activity.inputValues)
      this.activity.inputValues.push(...inputValues);

    await PersistentDataService.insertOrUpdateActivity(
      this.lineHandlingNo,
      this.activity
    );
    const newActs = await PersistentDataService.getActivities(
      this.lineHandlingNo
    );
    if (newActs) this.activities = newActs;

    const hasNominationChanges = this.checkStatus();
    if (hasNominationChanges && this.nomination) {
      await PersistentDataService.updateNomination(
        this.lineHandling,
        this.nomination
      );
    } else {
      await PersistentDataService.saveLineHandling(this.lineHandling);
    }

    return actionDesc;
  }

  public async triggerActionsAsync() {
    const { actionDetails } = this.action;
    if (actionDetails && actionDetails.triggerActions) {
      const { triggerActions } = actionDetails;

      for (let ctr = 0; ctr < triggerActions.length; ctr++) {
        const triggerAction = triggerActions[ctr];
        let activitiesToExecute = this.activities.filter(
          f =>
            f.processStepId === triggerAction.stepId &&
            f.nominationNo === this.nominationNo
        );

        for (let ctr1 = 0; ctr1 < activitiesToExecute.length; ctr1++) {
          let activityToExecute = activitiesToExecute[ctr1];
          if (
            this.isConditionMatched(
              triggerAction.conditions,
              undefined,
              undefined,
              activityToExecute.id
            )
          ) {
            const stepToExecute = this.config.processSteps.find(
              f => f.id === triggerAction.stepId
            );
            if (!stepToExecute) continue;

            const actionToExecute = stepToExecute.processActions.find(
              f => f.id === triggerAction.actionId
            );
            if (!actionToExecute) continue;

            const actionName =
              actionToExecute.name === ""
                ? activityToExecute.description
                : actionToExecute.name;

            activityToExecute.description = this.replaceMergeFields(actionName);
            activityToExecute.performedBy = "";
            activityToExecute.status = this.getActivityStatus(
              actionToExecute.type
            );
            activityToExecute.startedOn =
              actionToExecute.type === "Start"
                ? moment(this.executionTime).format("L LT")
                : activityToExecute.startedOn;
            activityToExecute.finishedOn =
              actionToExecute.type !== "Start"
                ? moment(this.executionTime).format("L LT")
                : activityToExecute.finishedOn;

            await PersistentDataService.insertOrUpdateActivity(
              this.lineHandlingNo,
              activityToExecute
            );
            const newActs = await PersistentDataService.getActivities(
              this.lineHandlingNo
            );
            if (newActs) this.activities = newActs;
          }
        }
      }
    }
  }

  public async processNextStepAsync() {
    const { actionDetails } = this.action;
    if (actionDetails && actionDetails.nextSteps) {
      let ctr = 0;
      for (; ctr < actionDetails.nextSteps.length; ctr++) {
        const nextStep = actionDetails.nextSteps[ctr];

        if (
          !this.IsPreConditionsMatched(
            nextStep.preConditions,
            this.nominationNo
          )
        )
          continue;
        if (nextStep.toStepIds.length === 0) continue;

        let ctr2 = 0;
        for (; ctr2 < nextStep.toStepIds.length; ctr2++) {
          const stepId = nextStep.toStepIds[ctr2];

          const nextProcessStep = this.config.processSteps.find(
            p => p.id === stepId
          );
          if (!nextProcessStep) continue;

          let stepName = nextProcessStep.name;
          let stateName = "";
          if (nextProcessStep.stateId && nextProcessStep.stateId > 0) {
            const processState = this.config.processStates.find(
              f => f.id === nextProcessStep.stateId
            );
            if (processState) stateName = processState.name;
            else stateName = this.lineHandling.currentState;
          } else {
            if (this.nomination) stateName = this.nomination.currentState;
            else stateName = this.lineHandling.currentState;
          }

          // if transitioning to LineHandling from Nomination
          // check if all nomination activities are done
          if (
            this.step.activityType === 1 &&
            nextProcessStep.activityType === 0
          ) {
            const finishedNomActivitiesCount = this.activities.filter(
              f => f.processStepId === this.step.id && f.status === 2
            ).length;

            if (
              finishedNomActivitiesCount != this.lineHandling.nominations.length
            )
              continue;

            this.lineHandling.currentState = stateName;
          }

          let newActivities: NewActivity[] = [];
          if (nextProcessStep.activityType === 1) {
            if (this.activity.type === 0 || this.activity.type == 2) {
              newActivities = this.lineHandling.nominations.map(
                n =>
                  new NewActivity(
                    n.nominationNo,
                    stepName.replace(MergeFields.Surveyor, n.surveyor),
                    null
                  )
              );
            } else {
              newActivities = [
                new NewActivity(
                  this.nominationNo,
                  stepName.replace(
                    MergeFields.Surveyor,
                    this.nomination ? this.nomination.surveyor : "surveyor"
                  ),
                  null
                )
              ];
            }
          } else if (nextProcessStep.activityType === 0) {
            newActivities = [new NewActivity("0", stepName, null)];

            if (stepName.indexOf(MergeFields.Surveyor) > 0) {
              const surveyorInputVal = this.activity.inputValues.find(
                f => f.caption === "Surveyor"
              );
              if (surveyorInputVal) {
                stepName = stepName.replace(
                  MergeFields.Surveyor,
                  surveyorInputVal.value
                );
                newActivities = [
                  new NewActivity("0", stepName, surveyorInputVal.value)
                ];
              }
            } else {
              const existingAct = this.activities.filter(
                f => f.processStepId === nextProcessStep.id
              );
              if (existingAct.length > 0) newActivities = [];
            }
          } else {
            let uniqueSurveyor: string[] = [];
            let ctr3 = 0;
            for (; ctr3 < this.lineHandling.nominations.length; ctr3++) {
              const surveyor = this.lineHandling.nominations[ctr3].surveyor;
              if (uniqueSurveyor.indexOf(surveyor) < 0)
                uniqueSurveyor.push(surveyor);
            }

            newActivities = uniqueSurveyor
              .filter(f => isValidSurveyor(f))
              .map(
                s =>
                  new NewActivity(
                    "0",
                    stepName.replace(MergeFields.Surveyor, s),
                    s
                  )
              );
          }

          let ctr4 = 0;
          for (; ctr4 < newActivities.length; ctr4++) {
            let newActivity = newActivities[ctr4];
            if (
              !this.isConditionMatched(
                nextStep.conditions,
                0,
                newActivity.nominationNo
              )
            )
              continue;

            if (
              newActivity.description.indexOf(MergeFields.OrdinalString) >= 0
            ) {
              const count =
                this.activities.filter(
                  f =>
                    f.processStepId === nextProcessStep.id &&
                    f.nominationNo.toLowerCase() ===
                      this.nominationNo.toLowerCase()
                ).length + 1;

              newActivity.description = newActivity.description.replace(
                MergeFields.OrdinalString,
                this.toOrdinalString(count)
              );
            }
            newActivity.description = this.replaceMergeFields(
              newActivity.description
            );

            let newAct = {
              id: 9000000 + this.activities.length + 1,
              nominationNo: newActivity.nominationNo,
              type: nextProcessStep.activityType,
              processStepId: nextProcessStep.id,
              actionId: 0,
              groupIndex: nextProcessStep.groupIndex,
              description: newActivity.description,
              startedOn:
                nextProcessStep.processType !== 0
                  ? moment(this.executionTime).format("L LT")
                  : "",
              finishedOn: "",
              performedBy: "",
              status: nextProcessStep.processType === 1 ? 1 : 0,
              state: stateName
            } as Activity;

            if (newActivity.surveyor !== "") {
              if (newAct.inputValues) {
                newAct.inputValues.push({
                  caption: "Surveyor",
                  value: newActivity.surveyor,
                  type: "text"
                } as InputValueValue);
              } else {
                newAct.inputValues = [
                  {
                    caption: "Surveyor",
                    value: newActivity.surveyor,
                    type: "text"
                  } as InputValueValue
                ];
              }
            }

            await PersistentDataService.insertOrUpdateActivity(
              this.lineHandlingNo,
              newAct
            );
            const newActs = await PersistentDataService.getActivities(
              this.lineHandlingNo
            );
            if (newActs) this.activities = newActs;

            if (ctr2 === 0) {
              if (
                nextProcessStep.activityType === 0 ||
                nextProcessStep.activityType === 2
              ) {
                this.lineHandling.currentStep = newActivity.description;
                await PersistentDataService.saveLineHandling(this.lineHandling);
              } else {
                let nom = this.lineHandling.nominations.find(
                  f =>
                    f.nominationNo.toLowerCase() ===
                    newActivity.nominationNo.toLowerCase()
                );
                if (nom) {
                  nom.currentStep = newActivity.description;
                  await PersistentDataService.updateNomination(
                    this.lineHandling,
                    nom
                  );
                }
              }
            }
          }
        }
      }
    }
  }

  public async moveStateAsync() {
    let nominationNos: string[] = [];
    const currentStateText = this.nomination
      ? this.nomination.currentState
      : this.lineHandling.currentState;
    const currentState = this.config.processStates.find(
      f => f.name === currentStateText
    );
    if (!currentState) return;

    if (currentState.requiredSteps && currentState.requiredSteps.length > 0) {
      if (this.nomination) {
        nominationNos.push(this.nominationNo);
      } else {
        if (currentState.scope.indexOf("LineHandling") >= 0) {
          nominationNos.push("0");
        }
        if (currentState.scope.indexOf("DirectTransfer") >= 0) {
          nominationNos.push(
            ...this.lineHandling.nominations
              .filter(f => f.transferType === "Direct Transfer")
              .map(n => n.nominationNo)
          );
        }
        if (currentState.scope.indexOf("TankTransfer") >= 0) {
          nominationNos.push(
            ...this.lineHandling.nominations
              .filter(f => f.transferType === "Tank Transfer")
              .map(n => n.nominationNo)
          );
        }
        if (currentState.scope.indexOf("OTSTransfer") >= 0) {
          nominationNos.push(
            ...this.lineHandling.nominations
              .filter(f => f.transferType === "OTS Transfer")
              .map(n => n.nominationNo)
          );
        }
        if (currentState.scope.indexOf("SLTransfer") >= 0) {
          nominationNos.push(
            ...this.lineHandling.nominations
              .filter(f => f.transferType === "SL Transfer")
              .map(n => n.nominationNo)
          );
        }
      }

      let ctr = 0;
      for (; ctr < nominationNos.length; ctr++) {
        const nomNo = nominationNos[ctr];
        let ctr1 = 0;
        for (; ctr1 < currentState.requiredSteps.length; ctr1++) {
          const requiredStepInfo = currentState.requiredSteps[ctr1];
          let isAnyMatched = false;
          let ctr2 = 0;
          for (; ctr2 < requiredStepInfo.stepConditions.length; ctr2++) {
            const statePrecondition = requiredStepInfo.stepConditions[ctr2];
            let isAllConditionsMatched = false;
            let ctr3 = 0;
            for (; ctr3 < statePrecondition.conditions.length; ctr3++) {
              const conditions = statePrecondition.conditions[ctr3];
              if (
                !this.isConditionMatched(
                  conditions.conditions,
                  conditions.stepId,
                  nomNo
                )
              ) {
                isAllConditionsMatched = false;
                break;
              }
              isAllConditionsMatched = true;
            }

            if (isAllConditionsMatched) {
              isAnyMatched = true;
              break;
            }
          }

          if (isAnyMatched) {
            const nextState = this.config.processStates.find(
              f => f.id === requiredStepInfo.toStateId
            );
            if (!nextState) continue;

            if (
              nomNo === "0" ||
              this.nominationNo.toLowerCase() === nomNo.toLowerCase()
            ) {
              this.activityResult.newState = nextState.name;
            }

            if (nomNo === "0") {
              this.lineHandling.currentState = nextState.name;
              await PersistentDataService.saveLineHandling(this.lineHandling);
            } else {
              let nom = this.lineHandling.nominations.find(
                f => f.nominationNo.toLowerCase() === nomNo.toLowerCase()
              );
              if (nom) {
                nom.currentState = nextState.name;
                await PersistentDataService.updateNomination(
                  this.lineHandling,
                  nom
                );
              }
            }
          }
        }
      }
    }
  }

  public async modifyActivityAsync() {
    const { actionDetails } = this.action;
    let hasChanges = false;
    if (actionDetails && actionDetails.modifyData) {
      const { modifyData } = actionDetails;
      if (modifyData.activities) {
        let ctr = 0;
        for (; ctr < modifyData.activities.length; ctr++) {
          const modActivity = modifyData.activities[ctr];
          let ctr1 = 0;
          let acts = this.activities.filter(
            f =>
              f.processStepId === modActivity.stepId &&
              (this.nominationNo === "0" ||
                f.nominationNo === this.nominationNo)
          );
          for (; ctr1 < acts.length; ctr1++) {
            let act: any;
            act = acts[ctr1];

            if (
              this.isConditionMatched(
                modActivity.conditions,
                0,
                act.nominationNo,
                act.id
              )
            ) {
              let ctr2 = 0;
              for (; ctr2 < modActivity.data.length; ctr2++) {
                const data = modActivity.data[ctr2];
                const field =
                  data.field.substr(0, 1).toLowerCase() + data.field.substr(1);
                act[field] = this.isInt(data.value)
                  ? parseInt(data.value)
                  : data.value;
              }

              await PersistentDataService.insertOrUpdateActivity(
                this.lineHandlingNo,
                act as Activity
              );
              hasChanges = true;
            }
          }
        }
      }

      if (modifyData.nomination) {
        let ctr = 0;
        for (; ctr < modifyData.nomination.length; ctr++) {
          const modNomination = modifyData.nomination[ctr];
          if (this.nomination && modNomination.data) {
            if (
              this.isConditionMatched(
                modNomination.conditions,
                0,
                this.nominationNo
              )
            ) {
              let ctr2 = 0;
              let nom: any;
              nom = this.nomination;

              for (; ctr2 < modNomination.data.length; ctr2++) {
                const data = modNomination.data[ctr2];
                const field =
                  data.field.substr(0, 1).toLowerCase() + data.field.substr(1);
                nom[field] = data.value;
              }

              await PersistentDataService.updateNomination(
                this.lineHandling,
                nom
              );
              hasChanges = true;
            }
          }
        }
      }

      if (hasChanges) {
        await PersistentDataService.saveLineHandling(this.lineHandling);
      }
    }
  }

  public async checkForActiveActivities() {
    const activeActsCount = this.activities.filter(
      f => f.status === 0 || f.status === 1 || f.status === 5
    ).length;

    if (activeActsCount === 0) {
      this.lineHandling.currentState = "Departed";
      this.lineHandling.status = 3;

      let ctr = 0;
      for (; ctr < this.lineHandling.nominations.length; ctr++) {
        this.lineHandling.nominations[ctr].currentState = "Departed";
      }

      await PersistentDataService.saveLineHandling(this.lineHandling);
    }
  }

  public async updateProgressAsync() {
    var loadSteps: number[] = [
      1,
      2,
      3,
      4,
      5,
      6,
      7,
      8,
      9,
      10,
      11,
      12,
      13,
      14,
      15,
      16,
      17,
      19,
      20,
      21,
      22,
      23,
      24,
      25,
      26,
      27,
      28,
      29,
      30,
      36,
      37,
      38,
      39,
      40,
      42,
      43,
      44,
      45,
      46,
      47,
      48,
      49,
      50,
      51,
      52,
      53,
      54,
      55,
      56
    ];
    var dischargeSteps: number[] = [
      1,
      2,
      3,
      4,
      5,
      6,
      7,
      8,
      9,
      10,
      11,
      12,
      13,
      14,
      18,
      19,
      20,
      21,
      22,
      23,
      24,
      25,
      31,
      32,
      33,
      34,
      35,
      36,
      37,
      38,
      39,
      40,
      41,
      42,
      43,
      44,
      45,
      46,
      47,
      48,
      49,
      50,
      51,
      52,
      53,
      54,
      55,
      56
    ];
    var otsSteps: number[] = [
      1,
      2,
      3,
      4,
      5,
      6,
      7,
      8,
      57,
      58,
      59,
      60,
      61,
      62,
      63,
      64,
      65,
      66,
      67,
      68,
      69,
      70,
      71,
      72,
      73,
      74,
      75,
      76,
      77
    ];
    var states = this.config.processStates;
    var lineHandlingSteps = this.config.processSteps.filter(
      s => s.activityType !== 1
    );

    var activityList: ProgressActivity[] = [];
    var activitiesData = this.activities.filter(
      a => a.status === 2 || a.status === 3
    );
    var activities = activitiesData.map(
      a =>
        new ProgressActivity(
          this.lineHandling.lineHandlingNo,
          a.nominationNo,
          a.processStepId,
          states.find(s => s.name === a.state)!.id,
          true
        )
    );

    var percentage = 0.0;
    this.lineHandling.nominations.forEach(nomination => {
      var nominationSteps = this.config.processSteps.filter(
        s => s.activityType === 1
      );
      var nominationActivities = activities.filter(
        a => a.nominationNo == nomination.nominationNo
      );

      if (nomination.transferType === "OTS Transfer") {
        nominationSteps = nominationSteps.filter(
          n => otsSteps.indexOf(n.id) > -1
        );
      } else if (nomination.nominationType === "Load") {
        nominationSteps = nominationSteps.filter(
          n => loadSteps.indexOf(n.id) > -1
        );
      } else {
        nominationSteps = nominationSteps.filter(
          n => dischargeSteps.indexOf(n.id) > -1
        );
      }

      var nominationState = states.find(
        s =>
          s.name ==
          (nomination.currentState == "Departed"
            ? "Departure"
            : nomination.currentState)
      );
      var prevNominationActivities = nominationActivities.filter(
        l => l.stateId < nominationState!.id
      );
      var nextNominationSteps = nominationSteps.filter(
        l => l.stateId! >= nominationState!.id
      );
      if (nominationState!.id < 4)
        nextNominationSteps.push(
          ...nominationSteps.filter(l => l.stateId == 0)
        );

      if (nomination.status !== 2) {
        activityList.push(...prevNominationActivities);
        var nextNominationActivities = nextNominationSteps.map(
          n =>
            new ProgressActivity(
              this.lineHandling.lineHandlingNo,
              nomination.nominationNo,
              n.id,
              n.stateId!,
              nominationActivities.find(l => l.stepId == n.id) === undefined
                ? false
                : true
            )
        );

        activityList.push(...nextNominationActivities);

        var finishedNominationList =
          nextNominationActivities.filter(n => n.isDone).length +
          prevNominationActivities.length;
        var nominationActivityList =
          prevNominationActivities.length + nextNominationActivities.length;

        percentage = Math.round(
          (finishedNominationList / nominationActivityList) * 100
        );
      } else {
        activityList.push(...nominationActivities);
        percentage = 100;
      }

      if (
        this.nomination &&
        nomination.nominationNo == this.nomination.nominationNo
      ) {
        this.nomination.progress = percentage;
        // await
        PersistentDataService.updateNomination(
          this.lineHandling,
          this.nomination
        );
      }
    });

    var lineHandlingActivities = activities.filter(a => a.nominationNo == "0");

    var lineHandlingState = states.find(
      s =>
        s.name ==
        (this.lineHandling.currentState == "Departed"
          ? "Departure"
          : this.lineHandling.currentState)
    );
    var prevLineHandlingActivities = lineHandlingActivities.filter(
      l => l.stateId < lineHandlingState!.id
    );
    var nextLineHandlingSteps = lineHandlingSteps.filter(
      l => l.stateId! >= lineHandlingState!.id
    );

    activityList.push(...prevLineHandlingActivities);
    if (this.lineHandling.status !== 3) {
      activityList.push(
        ...nextLineHandlingSteps.map(
          n =>
            new ProgressActivity(
              this.lineHandling.lineHandlingNo,
              "0",
              n.id,
              n.stateId!,
              lineHandlingActivities.find(l => l.stepId === n.id) === undefined
                ? false
                : true
            )
        )
      );

      var finishedActivityList = activityList.filter(n => n.isDone).length;
      var allActivityList = activityList.length;

      percentage = Math.round((finishedActivityList / allActivityList) * 100);
    } else {
      percentage = 100;
    }

    this.lineHandling.progress = percentage;
    await PersistentDataService.saveLineHandling(this.lineHandling);

    this.activityResult.lineHandling = this.lineHandling;
  }

  /*
   * Dynamic LINQ is not yet supported in javascript
   * For now check each conditions manually
   */
  public checkActionPreCondition(): boolean {
    const { actionDetails } = this.action;
    const defaultErrorMessage = "Can't proceed with the action.";

    if (actionDetails && actionDetails.conditionExp) {
      const { conditionExp } = actionDetails;

      if (conditionExp.activitiesConditions !== "") {
        // no current condition
      }

      if (this.nomination) {
        let ongoingLine = this.nomination.ongoingLine
          ? this.nomination.ongoingLine
          : "";
        if (conditionExp.nominationConditions === 'OngoingLine eq ""') {
          if (!(ongoingLine === "")) {
            throw new Error(
              conditionExp.errorMessage
                ? conditionExp.errorMessage
                : defaultErrorMessage
            );
          }
        } else if (
          conditionExp.nominationConditions ===
          '(OngoingLine eq "" or (OngoingLine eq "Shore Line" and NominationType eq 1))'
        ) {
          if (
            !(
              ongoingLine === "" ||
              (ongoingLine === "Shore Line" &&
                this.nomination.nominationType === "Discharge")
            )
          ) {
            throw new Error(
              conditionExp.errorMessage
                ? conditionExp.errorMessage
                : defaultErrorMessage
            );
          }
        } else if (
          conditionExp.nominationConditions ===
          '(OngoingLine eq "" or OngoingLine eq "Foot")'
        ) {
          if (!(ongoingLine === "" || ongoingLine === "Foot")) {
            throw new Error(
              conditionExp.errorMessage
                ? conditionExp.errorMessage
                : defaultErrorMessage
            );
          }
        } else if (
          conditionExp.nominationConditions ===
          'CurrentState ne "Prepare transfer" or (CurrentState eq "Prepare transfer" and !(CurrentStep in ("Ship Samples Accepted","Connect Cargo Hose","Connect Vapor Hose")) and NominationType eq 1) or (CurrentState eq "Prepare transfer" and NominationType eq 0 and OngoingLine eq "")'
        ) {
          const steps = [
            "Ship Samples Accepted",
            "Connect Cargo Hose",
            "Connect Vapor Hose"
          ];
          if (
            !(
              this.nomination.currentState !== "Prepare transfer" ||
              (this.nomination.currentState === "Prepare transfer" &&
                steps.indexOf(this.nomination.currentStep) < 0 &&
                this.nomination.nominationType === "Discharge") ||
              (this.nomination.currentState === "Prepare transfer" &&
                this.nomination.nominationType === "Load" &&
                this.nomination.ongoingLine === "")
            )
          ) {
            throw new Error(
              conditionExp.errorMessage
                ? conditionExp.errorMessage
                : defaultErrorMessage
            );
          }
        }
      }
    }

    return true;
  }

  tryGetActivityId(actionId: number, surveyor?: string): number | null {
    var activeStatus = [0, 1, 5];

    var processStep = this.config.processSteps.find(
      f => f.processActions.filter(c => c.id == actionId).length === 1
    );
    if (processStep) {
      var processStepId = processStep.id;
      var result = this.activities.filter(
        f =>
          f.processStepId == processStepId &&
          activeStatus.indexOf(f.status) >= 0
      );
      if (result.length === 1) {
        return result[0].id;
      } else if (result.length > 1) {
        const futherResult = result.filter(
          f => f.inputValues.find(ff => ff.value === surveyor) !== undefined
        );
        if (futherResult.length === 1) {
          return futherResult[0].id;
        }
      }
    }
    return null;
  }

  IsPreConditionsMatched(
    stepConditions: StepConditions[],
    nominationNo: string
  ): boolean {
    let ctr = 0;
    if (stepConditions && stepConditions.length > 0) {
      let isAnyMatched = false;
      for (; ctr < stepConditions.length; ctr++) {
        const stepCondition = stepConditions[ctr];
        let isAllMathed = false;
        let ctr1 = 0;
        for (; ctr1 < stepCondition.conditions.length; ctr1++) {
          const condition = stepCondition.conditions[ctr1];
          if (
            !this.isConditionMatched(
              condition.conditions,
              condition.stepId,
              nominationNo
            )
          ) {
            isAllMathed = false;
            break;
          }
          isAllMathed = true;
        }
        if (isAllMathed) {
          isAnyMatched = true;
          break;
        }
      }
      return isAnyMatched;
    }
    return true;
  }

  isConditionMatched(
    conditions: Conditions,
    stepId: number = 0,
    nominationNo: string = "0",
    activityId: number = 0
  ): boolean {
    if (conditions === Conditions.None) return true;

    stepId = stepId === 0 ? this.activity.processStepId : stepId;
    const acts =
      activityId === 0
        ? this.activities.filter(
            f =>
              f.processStepId === stepId &&
              f.nominationNo.toLowerCase() === this.nominationNo.toLowerCase()
          )
        : this.activities.filter(f => f.id === activityId);
    const nom =
      nominationNo === "0"
        ? this.nomination
        : this.lineHandling.nominations.find(
            f => f.nominationNo.toLowerCase() === nominationNo.toLowerCase()
          );
    if (
      (conditions & Conditions.NominationLoad) ===
      Conditions.NominationLoad
    ) {
      const result = nom && nom.nominationType === "Load";
      if (!result) return false;
    }
    if (
      (conditions & Conditions.NominationDischarge) ===
      Conditions.NominationDischarge
    ) {
      const result = nom && nom.nominationType === "Discharge";
      if (!result) return false;
    }

    if (
      (conditions & Conditions.NominationHasVaporLine) ===
      Conditions.NominationHasVaporLine
    ) {
      const result = nom && !isNAEquivalent(nom.shipVaporLine);
      if (!result) return false;
    }
    if (
      (conditions & Conditions.NominationNoVaporLine) ===
      Conditions.NominationNoVaporLine
    ) {
      const result = nom && isNAEquivalent(nom.shipVaporLine);
      if (!result) return false;
    }
    let ctr = 0;
    if (
      (conditions & Conditions.ActivityStatusCreated) ===
      Conditions.ActivityStatusCreated
    ) {
      if (stepId !== 0 && acts.length === 0) return false;

      for (ctr = 0; ctr < acts.length; ctr++)
        if (acts[ctr].status !== 0) return false;
    }

    if (
      (conditions & Conditions.ActivityStatusStarted) ===
      Conditions.ActivityStatusStarted
    ) {
      if (stepId !== 0 && acts.length === 0) return false;

      for (ctr = 0; ctr < acts.length; ctr++)
        if (acts[ctr].status !== 1) return false;
    }

    if (
      (conditions & Conditions.ActivityStatusDone) ===
      Conditions.ActivityStatusDone
    ) {
      if (stepId !== 0 && acts.length === 0) return false;

      for (ctr = 0; ctr < acts.length; ctr++)
        if (acts[ctr].status !== 2) return false;
    }

    if (
      (conditions & Conditions.ActivityStatusSkipped) ===
      Conditions.ActivityStatusSkipped
    ) {
      if (stepId !== 0 && acts.length === 0) return false;

      for (ctr = 0; ctr < acts.length; ctr++)
        if (acts[ctr].status !== 3) return false;
    }

    if (
      (conditions & Conditions.ActivityStatusVoid) ===
      Conditions.ActivityStatusVoid
    ) {
      if (stepId !== 0 && acts.length === 0) return false;

      for (ctr = 0; ctr < acts.length; ctr++)
        if (acts[ctr].status !== 4) return false;
    }

    if (
      (conditions & Conditions.ActivityStatusPaused) ===
      Conditions.ActivityStatusPaused
    ) {
      if (stepId !== 0 && acts.length === 0) return false;

      for (ctr = 0; ctr < acts.length; ctr++)
        if (acts[ctr].status !== 5) return false;
    }

    if ((conditions & Conditions.LineHandling) === Conditions.LineHandling) {
      if (nom) return false;
    }

    if (
      (conditions & Conditions.TransferTypeTank) ===
      Conditions.TransferTypeTank
    ) {
      const result = nom && nom.transferType === "Tank Transfer";
      if (!result) return false;
    }

    if (
      (conditions & Conditions.TransferTypeDirect) ===
      Conditions.TransferTypeDirect
    ) {
      const result = nom && nom.transferType === "Direct Transfer";
      if (!result) return false;
    }

    if (
      (conditions & Conditions.TransferTypeOTS) ===
      Conditions.TransferTypeOTS
    ) {
      const result = nom && nom.transferType === "OTS Transfer";
      if (!result) return false;
    }

    if (
      (conditions & Conditions.TransferTypeSL) ===
      Conditions.TransferTypeSL
    ) {
      const result = nom && nom.transferType === "SL Transfer";
      if (!result) return false;
    }

    if (
      (conditions & Conditions.ActivityNotExist) ===
      Conditions.ActivityNotExist
    ) {
      if (acts.length > 0) {
        return false;
      }
    }

    if (
      (conditions & Conditions.ActivityInPreArrival) ===
      Conditions.ActivityInPreArrival
    ) {
      if (stepId !== 0 && acts.length === 0) return false;

      for (ctr = 0; ctr < acts.length; ctr++)
        if (acts[ctr].state !== "Pre arrival") return false;
    }

    if (
      (conditions & Conditions.ActivityInArrival) ===
      Conditions.ActivityInArrival
    ) {
      if (stepId !== 0 && acts.length === 0) return false;

      for (ctr = 0; ctr < acts.length; ctr++)
        if (acts[ctr].state !== "Arrival") return false;
    }

    if (
      (conditions & Conditions.ActivityInPrepareTransfer) ===
      Conditions.ActivityInPrepareTransfer
    ) {
      if (stepId !== 0 && acts.length === 0) return false;

      for (ctr = 0; ctr < acts.length; ctr++)
        if (acts[ctr].state !== "Prepare transfer") return false;
    }

    if (
      (conditions & Conditions.NoSurveyorNeeded) ===
      Conditions.NoSurveyorNeeded
    ) {
      if (this.nominationNo === "0") {
        if (
          this.lineHandling.nominations.filter(f => isValidSurveyor(f.surveyor))
            .length > 0
        ) {
          return false;
        }
      } else {
        if (this.nomination && isValidSurveyor(this.nomination.surveyor)) {
          return false;
        }
      }
    }

    if ((conditions & Conditions.HasSurveyor) === Conditions.HasSurveyor) {
      if (this.nominationNo === "0") {
        if (
          this.lineHandling.nominations.filter(f => isValidSurveyor(f.surveyor))
            .length === 0
        ) {
          return false;
        }
      } else {
        if (
          !this.nomination ||
          (this.nomination && !isValidSurveyor(this.nomination.surveyor))
        ) {
          return false;
        }
      }
    }

    return true;
  }

  static IsConditionMatched(
    conditions: Conditions,
    lineHandling: LineHandling,
    nomination?: Nomination | null,
    activity?: Activity
  ): boolean {
    if (conditions === Conditions.None) return false;

    if (
      (conditions & Conditions.NominationLoad) ===
      Conditions.NominationLoad
    ) {
      const result = nomination && nomination.nominationType === "Load";
      if (!result) return false;
    }

    if (
      (conditions & Conditions.NominationDischarge) ===
      Conditions.NominationDischarge
    ) {
      const result = nomination && nomination.nominationType === "Discharge";
      if (!result) return false;
    }

    if (
      (conditions & Conditions.NominationHasVaporLine) ===
      Conditions.NominationHasVaporLine
    ) {
      const result = nomination && !isNAEquivalent(nomination.shipVaporLine);
      if (!result) return false;
    }

    if (
      (conditions & Conditions.NominationNoVaporLine) ===
      Conditions.NominationNoVaporLine
    ) {
      const result = nomination && isNAEquivalent(nomination.shipVaporLine);
      if (!result) return false;
    }

    if (
      (conditions & Conditions.ActivityStatusCreated) ===
      Conditions.ActivityStatusCreated
    ) {
      if (activity && activity.status !== 0) return false;
    }

    if (
      (conditions & Conditions.ActivityStatusStarted) ===
      Conditions.ActivityStatusStarted
    ) {
      if (activity && activity.status !== 1) return false;
    }

    if (
      (conditions & Conditions.ActivityStatusDone) ===
      Conditions.ActivityStatusDone
    ) {
      if (activity && activity.status !== 2) return false;
    }

    if (
      (conditions & Conditions.ActivityStatusSkipped) ===
      Conditions.ActivityStatusSkipped
    ) {
      if (activity && activity.status !== 3) return false;
    }

    if (
      (conditions & Conditions.ActivityStatusVoid) ===
      Conditions.ActivityStatusVoid
    ) {
      if (activity && activity.status !== 4) return false;
    }

    if (
      (conditions & Conditions.ActivityStatusPaused) ===
      Conditions.ActivityStatusPaused
    ) {
      if (activity && activity.status !== 5) return false;
    }

    if ((conditions & Conditions.LineHandling) === Conditions.LineHandling) {
      if (nomination) return false;
    }

    if (
      (conditions & Conditions.TransferTypeTank) ===
      Conditions.TransferTypeTank
    ) {
      const result = nomination && nomination.transferType === "Tank Transfer";
      if (!result) return false;
    }

    if (
      (conditions & Conditions.TransferTypeDirect) ===
      Conditions.TransferTypeDirect
    ) {
      const result =
        nomination && nomination.transferType === "Direct Transfer";
      if (!result) return false;
    }

    if (
      (conditions & Conditions.TransferTypeOTS) ===
      Conditions.TransferTypeOTS
    ) {
      const result = nomination && nomination.transferType === "OTS Transfer";
      if (!result) return false;
    }

    if (
      (conditions & Conditions.TransferTypeSL) ===
      Conditions.TransferTypeSL
    ) {
      const result = nomination && nomination.transferType === "SL Transfer";
      if (!result) return false;
    }

    if (
      (conditions & Conditions.ActivityNotExist) ===
      Conditions.ActivityNotExist
    ) {
      if (!activity) {
        return false;
      }
    }

    if (
      (conditions & Conditions.ActivityInPreArrival) ===
      Conditions.ActivityInPreArrival
    ) {
      const result = activity && activity.state === "Pre arrival";
      if (!result) return false;
    }

    if (
      (conditions & Conditions.ActivityInArrival) ===
      Conditions.ActivityInArrival
    ) {
      const result = activity && activity.state === "Arrival";
      if (!result) return false;
    }

    if (
      (conditions & Conditions.ActivityInPrepareTransfer) ===
      Conditions.ActivityInPrepareTransfer
    ) {
      const result = activity && activity.state === "Prepare transfer";
      if (!result) return false;
    }

    if (
      (conditions & Conditions.NoSurveyorNeeded) ===
      Conditions.NoSurveyorNeeded
    ) {
      if (!nomination) {
        var anySurveyor = lineHandling.nominations.filter(n =>
          isValidSurveyor(n.surveyor)
        );
        if (anySurveyor.length > 0) return false;
      } else {
        if (isValidSurveyor(nomination.surveyor)) {
          return false;
        }
      }
    }

    if ((conditions & Conditions.HasSurveyor) === Conditions.HasSurveyor) {
      if (!nomination) {
        var anySurveyor = lineHandling.nominations.filter(n =>
          isValidSurveyor(n.surveyor)
        );
        if (anySurveyor.length === 0) return false;
      } else {
        if (!isValidSurveyor(nomination.surveyor)) {
          return false;
        }
      }
    }

    return true;
  }

  checkStatus(): boolean {
    if (
      this.step.id === this.config.arrivedStepId &&
      this.action.type === "End"
    ) {
      this.lineHandling.status = 1;
    }

    if (
      this.step.id === this.config.endConferenceStepId &&
      this.action.type === "End"
    ) {
      this.lineHandling.status = 2;
    }

    if (this.nomination) {
      if (this.nomination.status === 0) {
        this.nomination.status = 1;
        return true;
      } else if (this.nomination.status === 1) {
        if (this.config.finishedStepIds.indexOf(this.step.id) >= 0) {
          //Hard coded for the meantime: Bug 1931
          if (this.step.id === 55) {
            var endCheckActivity = this.activities.find(
              a => a.processStepId == 46
            );

            if (endCheckActivity && endCheckActivity.status !== 2) {
              return false;
            }
          }
          if (this.step.id === 46) {
            var endCheckActivity = this.activities.find(
              a => a.processStepId == 55
            );

            if (!endCheckActivity || endCheckActivity.status !== 2) {
              return false;
            }
          }

          this.nomination.status = 2;
          return true;
        }
      }
    }

    return false;
  }

  replaceMergeFields(str: string): string {
    if (str) {
      if (str.indexOf(MergeFields.OrdinalString) >= 0) {
        const count = this.activities.filter(
          f =>
            (this.nominationNo === "0" ||
              f.nominationNo.toLowerCase() ===
                this.nominationNo.toLowerCase()) &&
            f.processStepId === this.activity.processStepId
        ).length;
        return str.replace(
          MergeFields.OrdinalString,
          this.toOrdinalString(count)
        );
      } else if (str.indexOf(MergeFields.Surveyor) >= 0) {
        if (this.nomination)
          return str.replace(MergeFields.Surveyor, this.nomination.surveyor);

        const inputValue = this.activity.inputValues.filter(
          f => f.caption === "Surveyor"
        );
        if (inputValue.length > 0) {
          return str.replace(MergeFields.Surveyor, inputValue[0].value);
        }
      } else if (str.indexOf(MergeFields.Direction) >= 0) {
        if (this.nomination) {
          return str.replace(
            MergeFields.Direction,
            this.nomination.nominationType
          );
        }
      } else if (str.indexOf(MergeFields.VesselType) >= 0) {
        if (this.lineHandling)
          return str.replace(
            MergeFields.VesselType,
            this.lineHandling.vesselType
          );
        else str.replace(MergeFields.VesselType, "Vessel");
      }
      return str;
    }
    return "";
  }

  static ReplaceMergeFields(
    str: string,
    activity: Activity,
    ordinal: string,
    lineHandling?: LineHandling | null,
    nomination?: Nomination | null
  ): string {
    if (str) {
      if (str.indexOf(MergeFields.OrdinalString) >= 0) {
        return str.replace(MergeFields.OrdinalString, ordinal);
      } else if (str.indexOf(MergeFields.Surveyor) >= 0) {
        if (nomination)
          return str.replace(MergeFields.Surveyor, nomination.surveyor);

        const inputValue = activity.inputValues.filter(
          f => f.caption === "Surveyor"
        );
        if (inputValue.length > 0) {
          return str.replace(MergeFields.Surveyor, inputValue[0].value);
        }
      } else if (str.indexOf(MergeFields.Direction) >= 0) {
        if (nomination) {
          return str.replace(MergeFields.Direction, nomination.nominationType);
        }
      } else if (str.indexOf(MergeFields.VesselType) >= 0) {
        if (lineHandling)
          return str.replace(MergeFields.VesselType, lineHandling.vesselType);
        else str.replace(MergeFields.VesselType, "Vessel");
      }
      return str;
    }
    return "";
  }

  toOrdinalString(number: number): string {
    if (number <= 0) return number.toString();

    switch (number % 100) {
      case 11:
      case 12:
      case 13:
        return number + "th";
    }

    switch (number % 10) {
      case 1:
        return number + "st";
      case 2:
        return number + "nd";
      case 3:
        return number + "rd";
      default:
        return number + "th";
    }
  }

  getActivityStatus(actionType: string): number {
    var result = 0;
    switch (actionType) {
      case "Start":
      case "Resume":
        result = 1;
        break;
      case "End":
        result = 2;
        break;
      case "Skip":
        result = 3;
        break;
      case "Void":
        result = 4;
        break;
      case "Pause":
        result = 5;
        break;
    }
    return result;
  }

  isInt(value: any): boolean {
    return (
      !isNaN(value) &&
      (function (x) {
        return (x | 0) === x;
      })(parseFloat(value))
    );
  }
}

const isValidSurveyor = (str?: string): boolean => {
  return (
    str !== undefined &&
    str !== "" &&
    str.toLowerCase() !== "no surveyor needed"
  );
};

const isNAEquivalent = (str?: string): boolean => {
  return (
    str === undefined ||
    str === null ||
    str === "" ||
    str.toLowerCase() === "n/a" ||
    str.toLowerCase() === "not applicable" ||
    str.toLowerCase() === "na" ||
    str.toLowerCase() === "n.a."
  );
};

class MergeFields {
  public static Surveyor = "{surveyor}";
  public static OrdinalString = "{n}";
  public static Direction = "{direction}";
  public static VesselType = "{vesseltype}";
}

class NewActivity {
  public nominationNo: string;
  public description: string;
  public surveyor: string | null;

  constructor(
    nominationNo: string,
    description: string,
    surveyor: string | null
  ) {
    this.nominationNo = nominationNo;
    this.description = description;
    this.surveyor = surveyor;
  }
}

class ProgressActivity {
  public lineHandlingNo: string;
  public nominationNo: string;
  public stepId: number;
  public stateId: number;
  public isDone: boolean;

  constructor(
    lineHandlingNo: string,
    nominationNo: string,
    stepId: number,
    stateId: number,
    isDone: boolean
  ) {
    this.lineHandlingNo = lineHandlingNo;
    this.nominationNo = nominationNo;
    this.stepId = stepId;
    this.stateId = stateId;
    this.isDone = isDone;
  }
}
